// Card 14, bottom nature (R548, ui-map-interface.md §17.3.14).
//
// A chart scatters two-letter abbreviations for what the lead brought up: S
// sand, M mud, Sh shells, Co coral, R rock, Oz ooze. Here the letter beside a
// deposit is its commodity — `Al`, `Bi` or `Vo`, the `short` of
// `data/identity/identity.toml` — so a reader knows what a field yields
// before the tooltip does. `R` on a ridge says rock, the one word that
// matters about it; `Oz` on a trench floor says soft ground, the water a
// fleet runs quiet in.
//
// **THE LETTER IS THE PACK'S, NEVER A LITERAL.** The commodity shorts come
// from `RESOURCES`, and the two ground words from `[ui.chart.text]`. The ink
// of a deposit is the commodity's own colour, so the letter and the field
// diamond under it can never disagree.
//
// The card sets both readings in italic. The description of the card, which
// is the ruling, says the current reading takes the same letters UPRIGHT, so
// the modern reading uses the monospaced face and the historical one the
// engraved italic — the two faces the whole roster uses (§17.4).

import { CHART, TERRAIN_TRENCH_RIM } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import { SiteKind } from '../../../gen/proto/aquaphobia';
import { cellCenter, resourceOf } from '../../../world';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, faceOf } from './shared';

/** The figures the piece reads out of its own table. */
const T = CHART.pieces.bottom_nature;

/** One letter pair, and where it stands. */
interface Mark {
  text: string;
  x: number;
  y: number;
  ink: number;
  alpha: number;
  /** `left` beside a deposit, `centre` over a body of ground. */
  anchor: 'left' | 'centre';
}

export class BottomNaturePiece implements Piece {
  readonly id = 'bottom_nature' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The ground the fight happened over. It stays under a replay film. */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'sites', 'reading', 'zoomband'] as const;

  private marks: Mark[] = [];

  rebuild(ctx: ChartContext): void {
    const marks: Mark[] = [];
    for (const site of ctx.state.sites.values()) {
      if (site.kind !== SiteKind.SITE_KIND_DEPOSIT) continue;
      // A commodity the pack does not name writes no letter.
      const commodity = resourceOf(site.resource);
      if (!commodity) continue;
      const [cx, cy] = cellCenter(site.cell);
      marks.push({
        text: commodity.short, x: cx + T.fig.dx, y: cy + T.fig.dy,
        ink: commodity.color, alpha: T.fig.alpha, anchor: 'left',
      });
    }
    const terrain = ctx.terrain;
    if (terrain) {
      for (const f of terrain.ridges) {
        marks.push({
          text: S.nature_ridge, x: f.centroid.x, y: f.centroid.y,
          ink: CHART_INK.ridge, alpha: T.fig.ridge_alpha, anchor: 'centre',
        });
      }
      for (const f of terrain.trenches) {
        marks.push({
          text: S.nature_trench, x: f.centroid.x, y: f.centroid.y,
          ink: TERRAIN_TRENCH_RIM.color, alpha: T.fig.alpha, anchor: 'centre',
        });
      }
    }
    this.marks = marks;
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const face = faceOf(ctx.reading);
    for (const m of this.marks) {
      ctx.glyphs.word(
        sinks.quads, m.text, m.x, m.y, face, T.text.main, m.ink, m.alpha,
        { anchor: m.anchor },
      );
    }
  }

  /** How many letter pairs the last rebuild found. The test reads it. */
  get count(): number { return this.marks.length; }
}

/** The one instance the registry holds. */
export const bottomNature = new BottomNaturePiece();
